import axios from 'axios';
import { ENV } from '@config/env';
import { useAuth } from '@/zustand/auth-store';
import { showError } from '@/libs/react.toastify';
import { AuthApis } from '@/features/auth/login/services/api';
import { clearMovexOverviewPaginationStorage } from '@/utils/movexOverviewPaginationStorage';

const ApiInstance = axios.create({
  baseURL: ENV.apiBaseUrl,
});

// Refresh Token Queue + State
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

const extractTokens = response => {
  const tokenSource = response?.tokens || response?.token || response || {};

  return {
    accessToken: tokenSource?.accessToken ?? tokenSource?.access ?? '',
    refreshToken: tokenSource?.refreshToken ?? tokenSource?.refresh ?? '',
  };
};

// Helpers
const attachLanguageHeader = config => {
  config.headers['Accept-Language'] = localStorage.getItem('i18nextLng') || 'en';
};

const attachAccessToken = config => {
  if (config?.skipAuthHeader) {
    delete config.headers.Authorization;
    return;
  }

  const token = useAuth.getState().accessToken;
  const preAuthToken = useAuth.getState()?.preAuthToken;
  if (config?.useAlternateToken) {
    if (preAuthToken) {
      config.headers.Authorization = `Bearer ${preAuthToken}`;
    }
    return;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
};

// REQUEST INTERCEPTOR
ApiInstance.interceptors.request.use(
  config => {
    attachLanguageHeader(config);
    attachAccessToken(config);

    // CRITICAL: For FormData, we must delete Content-Type header
    // so the browser can set it automatically with the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// RESPONSE INTERCEPTOR (401 Refresh Logic)
export const setupInterceptors = navigate => {
  ApiInstance.interceptors.response.use(
    res => res,
    async error => {
      const originalReq = error.config;

      // 500 SERVER ERROR
      if (error.response?.status === 500) {
        showError('Server error. Please try again later.');
        return Promise.reject(error);
      }

      // 401 UNAUTHORIZED → TRY REFRESH (by user type)
      if (error.response?.status === 401 && !originalReq._retry) {
        clearMovexOverviewPaginationStorage();

        // Guest visitor: /home and the other shared routes render without a
        // session, so their requests come back 401 by design. There's no
        // refresh token to spend and nothing to clear — kicking them to
        // /login here would make those pages unreachable. Let the caller
        // handle the empty/unauthorized state instead.
        const { accessToken, refreshToken } = useAuth.getState();
        if (!accessToken && !refreshToken) {
          return Promise.reject(error);
        }

        if (originalReq?.skipRefresh) {
          if (!originalReq?.skipRedirect) {
            useAuth.getState().clearAuth();
            navigate('/login', { replace: true });
          }
          return Promise.reject(error);
        }

        originalReq._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(newToken => {
              originalReq.headers.Authorization = `Bearer ${newToken}`;
              return ApiInstance(originalReq);
            })
            .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        const refreshFn = AuthApis.refreshAccessToken;

        try {
          const response = await refreshFn();
          const { accessToken, refreshToken } = extractTokens(response);

          if (!accessToken || !refreshToken) {
            throw new Error('Refresh token response did not include new tokens');
          }

          useAuth.getState().setTokens({ accessToken, refreshToken });
          ApiInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

          processQueue(null, accessToken);
          isRefreshing = false;

          originalReq.headers.Authorization = `Bearer ${accessToken}`;
          return ApiInstance(originalReq);
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          isRefreshing = false;

          useAuth.getState().clearAuth();
          navigate('/login', { replace: true });

          return Promise.reject(refreshErr);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default ApiInstance;
